import { saveAs } from 'file-saver';
import { calculatePageOffset, conditionalOptions, setRelativeComplement } from '../../../utils';
import { VendorService } from './VendorService';
import { IPortalUser, portalUserService } from '../../UserService';
import {
    ISearchConfiguration,
    ISearchRequest,
    ISearchResponse,
    ISearchService,
    createTextField,
    createCheckboxField,
    createAutocompleteField,
    createSimpleDropdownOptions,
    createUseSearchService,
    SimpleFieldRendererViewModel,
    createRadioButtonGroup,
    createDropdownField,
    DependenciesWatcher,
    createEnumDropdownField,
} from '../../../reusableFeatures';
import { Link } from '@mui/icons-material';
import { GridRenderCellParams } from '@mui/x-data-grid';
import { TOOLTIP_CELL_MODE } from '../../../ui';
import { getVendorSearchModeText } from '../../../utils/configurableMessages/getVendorSearchModeText';
import { InvoiceProfileType } from '../../Company/Api';
import { Box, List, ListItem, ListItemText, Typography } from '@mui/material';
import { IProfile, ProfileService, ProfileType } from '../../Profile';
import { multiCompanySelectDefaultFieldConfig } from '../../../features';
import { backendServices, orderBy } from '../..';
import { RelativeUserHierarchy } from '../../../hooks';
import { SimDocumentCreated, SupplierCompanyTradingPartnerApprovalStatus } from '../../../types';

export enum VendorSearchMode {
    VendorFeed = 0,
    ConnectedSuppliers = 1,
    CloudCaptureSuppliers = 2,
    VendorMissingRequiredInfo = 3,
}

/**
 * OData $filter for the "Search Vendors Missing Required Info" mode. Runs on top of the vendor
 * search (includeVendors=true) so results carry vendor ids and are editable, and catches blank
 * ('') values as well as NULLs, which the search function's own missing-info check does not.
 */
export const MISSING_REQUIRED_INFO_ODATA_FILTER =
    "VendorNumber eq null or VendorNumber eq '' or VendorClass eq null or VendorClass eq ''";

enum ViewingOptions {
    'All vendors' = 1,
    "All peer's assigned vendors" = 2,
    "All report's and my assigned vendors" = 3,
    "All report's assigned vendors" = 4,
    'My assigned vendors' = 5,
    "My direct report's and my assigned vendors" = 6,
    "My direct report's assigned vendors" = 7,
}

type IVendorSearchData = {
    name?: string;

    vendorNumber?: string;

    vendorClass?: string | null;

    contactName?: string;

    contactEmail?: string;

    includeDisabledSuppliers: boolean;

    searchMode: VendorSearchMode;

    siteNumber?: string;

    orgUnit?: string | null;

    salesForcePhaseName?: string;

    viewing?: ViewingOptions;

    simDocumentCreated?: SimDocumentCreated;

    approvalStatus?: SupplierCompanyTradingPartnerApprovalStatus;
} & Record<typeof multiCompanySelectDefaultFieldConfig.dataBindingKey, number[] | undefined>;

export class VendorSearchService implements ISearchService<IVendorSearchData> {
    private companyApi = new backendServices.Apis.CompanyApi();

    private vendorService = new VendorService();

    private profileService = new ProfileService();

    private multiCompanyIsEnabled = false;

    private vendorSearchMode = VendorSearchMode.VendorFeed;

    private providedProfiles: IProfile[] | null = null;

    constructor(
        /**
         * Used to keep the SearchPageTemplate in a loading state until useQuery instances have finished fetching.
         */
        private readonly dependenciesWatcher: DependenciesWatcher,
        private readonly orgUnitOptions: { label: string; value: string }[],
        private readonly isCloudCaptureSupplierInviteEnabled: boolean,
        private readonly isMissingVendorInfoSearchEnabled: boolean,
        /**
         * Optionally provide profiles that have been pre-fetched
         */
        profiles: IProfile[] | undefined,
        private readonly displaySalesForcePhaseName: boolean,
        private readonly enableVendorApprovalWorkflow: boolean,
        private readonly relativeUserHierarchy: RelativeUserHierarchy,
        private readonly users: Pick<backendServices.ViewModels.UserViewModel, 'ID' | 'Name'>[],
        private readonly allUserGroups: Pick<
            backendServices.ViewModels.UserGroupViewModel,
            'Id' | 'Name' | 'CompanyId'
        >[]
    ) {
        this.multiCompanyIsEnabled = portalUserService.getCompanyChildren().length > 0;
        this.providedProfiles = profiles || null;
    }

    public getVendorSearchMode(): VendorSearchMode {
        return this.vendorSearchMode;
    }

    private setVendorSearchMode(mode: VendorSearchMode) {
        this.vendorSearchMode = mode;
    }

    private getUserIdsByViewingOptions(viewingOption: ViewingOptions | null): string {
        const currentUser = portalUserService.getCurrentUser('mustBeLoggedIn');

        const peerIds = this.relativeUserHierarchy.peers.map((p) => p.ID).join(',');
        const directReportIds = this.relativeUserHierarchy.directReports.map((p) => p.ID).join(',');
        const reportIds = this.relativeUserHierarchy.reports.map((p) => p.ID).join(',');

        switch (viewingOption) {
            case ViewingOptions['All vendors']:
                return '';
            case ViewingOptions["All peer's assigned vendors"]:
                return peerIds;
            case ViewingOptions["All report's and my assigned vendors"]:
                return `${currentUser.id}${reportIds.length ? `,${reportIds}` : ''}`;
            case ViewingOptions["All report's assigned vendors"]:
                return reportIds;
            case ViewingOptions['My assigned vendors']:
                return currentUser.id.toString();
            case ViewingOptions["My direct report's and my assigned vendors"]:
                return `${currentUser.id}${directReportIds.length ? `,${directReportIds}` : ''}`;
            case ViewingOptions["My direct report's assigned vendors"]:
                return directReportIds;
            default:
                return '';
        }
    }

    async fetchSearchConfiguration(): Promise<ISearchConfiguration<IVendorSearchData>> {
        const currentUser = portalUserService.getCurrentUser('mustBeLoggedIn');

        const companyId = portalUserService.getCurrentCompanyId();

        const childCompanyIds = portalUserService.getCompanyChildren().map((c) => c.value);

        const childCompanyIdsAndParentCompany = [companyId, ...childCompanyIds];

        const { data: vendorClasses } = await this.companyApi.getVendorClass({
            companyId,
            childCompanyId: this.multiCompanyIsEnabled ? childCompanyIdsAndParentCompany.join(',') : undefined,
        });

        // Use provided profiles if available, otherwise fetch them
        const profiles =
            this.providedProfiles ||
            (await this.profileService.searchProfiles(
                ProfileType.BuyerDestination,
                companyId,
                this.multiCompanyIsEnabled ? childCompanyIdsAndParentCompany.join(',') : undefined
            ));

        await this.dependenciesWatcher.waitForDependencies();

        function handleRenderLinkColumn(params: GridRenderCellParams) {
            // params.id will have the following format:
            // CompanyID-VendorID-VendorNumber
            // If any of those 3 IDs are null (missing), then vendor and supplier are not connected
            // Also if search radio button 'Search Connected Suppliers' is ON, id returned includes nulls so we won't display this link icon
            if (!params.id.toString().includes('null')) {
                if (params.cellMode === TOOLTIP_CELL_MODE) {
                    return 'Vendor and Supplier Connected';
                }

                return <Link />;
            }
            return null;
        }

        const showOrgUnit = this.orgUnitOptions.length > 0;

        const isVendorApprovalEnabled = this.enableVendorApprovalWorkflow;

        const userNameMap = new Map<number, string>(this.users.map((u) => [u.ID, u.Name]));
        const userGroupMap = new Map<number, string>(this.allUserGroups.map((u) => [u.Id, u.Name]));

        return {
            gridColumns: (criteria) => {
                const { MultiCompanySelect = [] } = criteria.state;

                // if the company selector is empty, use the parent company id.
                const selectedCompanyIds =
                    MultiCompanySelect.length && this.multiCompanyIsEnabled ? MultiCompanySelect : [companyId];

                const filteredProfiles = profiles.filter((p) => selectedCompanyIds.includes(p.companyId));

                const shouldDisplayScanKeyOrNormalColumn = filteredProfiles.some(
                    (profile) => !!profile.scanKeyProfileId
                );

                return [
                    ...conditionalOptions(isVendorApprovalEnabled, [
                        {
                            field: 'userIds',
                            headerName: 'Assigned To',
                            flex: 1,
                            renderCell: ({ value: userIds, cellMode, row }: GridRenderCellParams<number[]>) => {
                                const values = userIds?.map((id) => userNameMap.get(id) ?? '').filter(Boolean) ?? [];

                                const userGroupIds = row.userGroupIds as number[] | null;

                                if (Array.isArray(userGroupIds) && userGroupIds.length) {
                                    const userGroupNames = userGroupIds
                                        .map((id) => userGroupMap.get(id) ?? '')
                                        .filter(Boolean)
                                        .map((name) => `UG: ${name}`);

                                    values.push(...userGroupNames);
                                }

                                if (!values.length) {
                                    return null;
                                }

                                if (cellMode === TOOLTIP_CELL_MODE) {
                                    return (
                                        <List
                                            dense
                                            sx={{
                                                maxHeight: '300px',
                                                overflow: 'auto',
                                            }}
                                        >
                                            {values.map((v) => (
                                                <ListItem key={v}>
                                                    <ListItemText primary={v} />
                                                </ListItem>
                                            ))}
                                        </List>
                                    );
                                }

                                return (
                                    <Box
                                        sx={{
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                        }}
                                    >
                                        {values.join(', ')}
                                    </Box>
                                );
                            },
                        },
                    ]),
                    {
                        field: 'name',
                        headerName: 'Name',
                        flex: 1,
                    },
                    {
                        field: 'link',
                        headerName: '',
                        width: 40,
                        renderCell: handleRenderLinkColumn,
                        sortable: false,
                    },
                    { field: 'vendorNumber', headerName: 'Vendor Number', flex: 0.5 },
                    { field: 'vendorClass', headerName: 'Vendor Class', flex: 0.5 },
                    ...conditionalOptions(shouldDisplayScanKeyOrNormalColumn, [
                        {
                            field: 'invoiceProfileType',
                            headerName: 'CloudCapture or Normal',
                            flex: 0.5,
                            renderCell: (params: GridRenderCellParams<InvoiceProfileType>) => (
                                <Typography>
                                    {params.value === InvoiceProfileType.ScanKey ? 'CloudCapture' : 'Normal'}
                                </Typography>
                            ),
                        },
                    ]),
                    ...conditionalOptions(this.shouldUseVendorSearchWithCriteria(currentUser, criteria), [
                        { field: 'siteNumber', headerName: 'Site Number', flex: 0.5 },
                    ]),
                    { field: 'contactName', headerName: 'Contact Name', flex: 1, sortable: false },
                    ...conditionalOptions(isVendorApprovalEnabled, [
                        {
                            field: 'approvalStatus',
                            headerName: 'Vendor Status',
                            flex: 0.5,
                            sortable: false,
                            valueGetter: ({
                                value,
                            }: GridRenderCellParams<SupplierCompanyTradingPartnerApprovalStatus | null>) => {
                                if (value == null) {
                                    return null;
                                }

                                const displayValue = SupplierCompanyTradingPartnerApprovalStatus[value];

                                if (!displayValue) {
                                    return null;
                                }

                                return displayValue;
                            },
                        },
                    ]),
                    { field: 'contactEmail', headerName: 'Contact Email', flex: 1.5, sortable: false },
                    ...conditionalOptions(showOrgUnit, [
                        {
                            field: 'orgUnit',
                            headerName: 'Org',
                            flex: 1,
                            sortable: false,
                            renderCell: (params: GridRenderCellParams<string | null>) =>
                                this.orgUnitOptions.find((opt) => opt.value === params.value)?.label ?? '',
                        },
                    ]),
                ];
            },
            enabledExportingMethods: [],
            searchFormFieldColumns: (criteria) => {
                const { MultiCompanySelect = [] } = criteria.state;

                // if the company selector isn't enabled, use the parent company id.
                const selectedCompanyIds = this.multiCompanyIsEnabled ? MultiCompanySelect : [companyId];

                const uniqueVendorClassNames = new Set<string>();

                const vendorClassOptions = vendorClasses
                    .filter((v) => {
                        if (!selectedCompanyIds.includes(v.CompanyId)) {
                            return false;
                        }

                        const lowerCaseVendorClassName = v.VendorClassName.toLowerCase();

                        if (uniqueVendorClassNames.has(lowerCaseVendorClassName)) {
                            return false;
                        }

                        uniqueVendorClassNames.add(lowerCaseVendorClassName);

                        return true;
                    })
                    .map((v) => v.VendorClassName);

                const leftColumn = [
                    createTextField({
                        id: 'name',
                        label: 'Name',
                        defaultValue: '',
                        dataBindingKey: 'name',
                    }),
                    ...conditionalOptions(criteria.state.searchMode !== VendorSearchMode.VendorMissingRequiredInfo, [
                        createTextField({
                            id: 'vendorNumber',
                            label: 'Vendor Number',
                            defaultValue: '',
                            dataBindingKey: 'vendorNumber',
                        }),
                    ]),
                    ...conditionalOptions(this.multiCompanyIsEnabled, [
                        createDropdownField({
                            ...multiCompanySelectDefaultFieldConfig,
                            initialValue: childCompanyIdsAndParentCompany,
                        }),
                    ]),
                    ...conditionalOptions(
                        vendorClasses.length > 0 &&
                            criteria.state.searchMode !== VendorSearchMode.VendorMissingRequiredInfo,
                        [
                            createAutocompleteField({
                                id: 'vendorClass',
                                label: 'Vendor Class',
                                defaultValue: null,
                                dataBindingKey: 'vendorClass',
                                options: createSimpleDropdownOptions(vendorClassOptions),
                            }),
                        ]
                    ),
                    ...conditionalOptions(this.shouldUseVendorSearchWithCriteria(currentUser, criteria), [
                        createTextField({
                            id: 'siteNumber',
                            label: 'Site Number',
                            defaultValue: '',
                            dataBindingKey: 'siteNumber',
                        }),
                    ]),
                    ...conditionalOptions(showOrgUnit, [
                        createDropdownField({
                            id: 'orgUnit',
                            label: 'Org',
                            defaultValue: null,
                            nullOptionLabel: '',
                            dataBindingKey: 'orgUnit',
                            options: this.orgUnitOptions,
                        }),
                    ]),
                    ...conditionalOptions(isVendorApprovalEnabled, [
                        createEnumDropdownField({
                            id: 'approvalStatus',
                            label: 'Vendor Status',
                            defaultValue: null,
                            dataBindingKey: 'approvalStatus',
                            options: SupplierCompanyTradingPartnerApprovalStatus,
                            nullOptionLabel: '',
                        }),
                    ]),
                    ...conditionalOptions(isVendorApprovalEnabled, [
                        createEnumDropdownField({
                            id: 'simDocumentCreated',
                            label: 'SIM Documents',
                            defaultValue: null,
                            dataBindingKey: 'simDocumentCreated',
                            options: SimDocumentCreated,
                            nullOptionLabel: '',
                        }),
                    ]),
                    ...conditionalOptions(isVendorApprovalEnabled, [
                        createEnumDropdownField({
                            id: 'viewing',
                            label: 'Viewing',
                            defaultValue: null,
                            nullOptionLabel: '',
                            dataBindingKey: 'viewing',
                            options: ViewingOptions,
                        }),
                    ]),
                ];

                const vendorSearchModeText = getVendorSearchModeText();

                const searchTooltip = vendorSearchModeText.buildTooltip({
                    includeCloudCapture: this.isCloudCaptureSupplierInviteEnabled,
                    includeMissingInfo: this.isMissingVendorInfoSearchEnabled,
                });

                const rightColumn = [
                    createTextField({
                        id: 'contactName',
                        label: 'Contact Name',
                        defaultValue: '',
                        dataBindingKey: 'contactName',
                    }),
                    createTextField({
                        id: 'contactEmail',
                        label: 'Contact Email',
                        defaultValue: '',
                        dataBindingKey: 'contactEmail',
                    }),
                    ...conditionalOptions(this.displaySalesForcePhaseName, [
                        createTextField({
                            id: 'salesForcePhaseName',
                            label: 'Recruiting Phase',
                            defaultValue: '',
                            dataBindingKey: 'salesForcePhaseName',
                        }),
                    ]),
                    ...conditionalOptions(currentUser.extraFeatures.has('FE_TM_VENDOR_MANAGEMENT_ENABLED'), [
                        createRadioButtonGroup({
                            id: 'searchMode',
                            dataBindingKey: 'searchMode',
                            label: vendorSearchModeText.label,
                            defaultValue: VendorSearchMode.VendorFeed,
                            options: [
                                {
                                    label: vendorSearchModeText.searchVendorsText,
                                    value: VendorSearchMode.VendorFeed,
                                },
                                {
                                    label: vendorSearchModeText.searchSuppliersText,
                                    value: VendorSearchMode.ConnectedSuppliers,
                                },
                                ...(this.isCloudCaptureSupplierInviteEnabled
                                    ? [
                                          {
                                              label: vendorSearchModeText.searchCloudCaptureText,
                                              value: VendorSearchMode.CloudCaptureSuppliers,
                                          },
                                      ]
                                    : []),
                                ...(this.isMissingVendorInfoSearchEnabled
                                    ? [
                                          {
                                              label: vendorSearchModeText.searchMissingInfoText,
                                              value: VendorSearchMode.VendorMissingRequiredInfo,
                                          },
                                      ]
                                    : []),
                            ],
                            tooltip: searchTooltip && {
                                message: searchTooltip,
                                icon: 'Help',
                            },
                        }),
                    ]),
                    ...conditionalOptions(criteria.state.searchMode !== VendorSearchMode.CloudCaptureSuppliers, [
                        createCheckboxField({
                            id: 'includeDisabledSuppliers',
                            label: this.shouldUseVendorSearchWithCriteria(currentUser, criteria)
                                ? 'Include Disabled Vendors'
                                : 'Include Disabled Suppliers',
                            defaultValue: false,
                            dataBindingKey: 'includeDisabledSuppliers',
                        }),
                    ]),
                ];

                return [leftColumn, rightColumn];
            },
            pageSizeOptions: [10, 25, 50],
            defaultPageSize: 10,
            searchWithNoCriteria: false,
            isCriteriaEmpty: (_criteria, filledFields) => {
                // if no company is selected, the user shouldn't be able to search
                if (
                    this.multiCompanyIsEnabled &&
                    !filledFields.includes(multiCompanySelectDefaultFieldConfig.dataBindingKey)
                ) {
                    return true;
                }

                // For missing vendor info search mode, allow search with just search mode selected
                if (_criteria.state.searchMode === VendorSearchMode.VendorMissingRequiredInfo) {
                    return false;
                }

                // the API won't allow searching with only the checkbox fields selected. currently we wish
                // to force the user to "narrow" the search criteria to reduce DB load. in the future when
                // we have server side caching applied to the trading partner search endpoint, we will allow
                // searching without any criteria and remove this check.
                return (
                    setRelativeComplement(filledFields, [
                        'searchMode',
                        'includeDisabledSuppliers',
                        // Multi-company selector should be ignored
                        multiCompanySelectDefaultFieldConfig.dataBindingKey,
                    ]).length === 0
                );
            },
        };
    }

    private buildSearchParams(
        searchRequest: ISearchRequest<IVendorSearchData>
    ): Omit<backendServices.Apis.GetTradingPartnersSearchParams, '$count' | '$top' | '$skip'> {
        const { MultiCompanySelect = [] } = searchRequest.searchQuery.state;

        const companyId = portalUserService.getCurrentCompanyId();

        const currentUser = portalUserService.getCurrentUser('mustBeLoggedIn');
        const isVendorManagementEnabled = currentUser.extraFeatures.has('FE_TM_VENDOR_MANAGEMENT_ENABLED');

        const internalFieldMapping: { [key: string]: string | undefined } = {
            supplierId: 'CompanyID',
            vendorId: 'VendorId',
            name: 'Name',
            vendorNumber: 'VendorNumber',
            vendorClass: 'VendorClass',
            contactName: 'ContactName',
            contactEmail: 'ContactEmail',
            siteNumber: 'VendorSiteNumber',
            orgUnit: 'OrgUnit',
            approvalStatus: 'ApprovalStatus',
        };

        const mappedOrderBy = orderBy(
            searchRequest.sort.map(({ field, sort }) => ({ field: internalFieldMapping[field] ?? field, sort }))
        );

        const defaultSearchMode = isVendorManagementEnabled
            ? VendorSearchMode.VendorFeed
            : VendorSearchMode.ConnectedSuppliers;
        const vendorSearchMode = searchRequest.searchQuery.state.searchMode ?? defaultSearchMode;
        const isMissingInfoMode = vendorSearchMode === VendorSearchMode.VendorMissingRequiredInfo;

        const userIds = this.getUserIdsByViewingOptions(searchRequest.searchQuery.state.viewing ?? null);

        const approvalStatus = searchRequest.searchQuery.state.approvalStatus;
        const simDocumentCreated = searchRequest.searchQuery.state.simDocumentCreated;

        const isWorkflowSearch = this.enableVendorApprovalWorkflow;

        return {
            companyId,
            $orderBy: mappedOrderBy,
            $filter: isMissingInfoMode ? MISSING_REQUIRED_INFO_ODATA_FILTER : undefined,
            // In missing-info mode, companyName falls back to '%' (LIKE match-all) because the
            // search function only takes its plain-filters path when at least one filter is set;
            // its no-filters path pre-narrows to NULL-only vendors before $filter can run.
            companyName: isMissingInfoMode
                ? searchRequest.searchQuery.state.name || '%'
                : searchRequest.searchQuery.state.name,
            contactName: searchRequest.searchQuery.state.contactName,
            contactEmail: searchRequest.searchQuery.state.contactEmail,
            vendorNumber: !isMissingInfoMode ? searchRequest.searchQuery.state.vendorNumber : undefined,
            vendorClass: !isMissingInfoMode ? searchRequest.searchQuery.state.vendorClass ?? undefined : undefined,
            vendorSiteNumber: searchRequest.searchQuery.state.siteNumber,
            includeVendors: vendorSearchMode === VendorSearchMode.VendorFeed || isMissingInfoMode,
            includeDisabled: searchRequest.searchQuery.state.includeDisabledSuppliers,
            includeConnectedCCVendorsOnly: vendorSearchMode === VendorSearchMode.CloudCaptureSuppliers,
            childCompanyId:
                this.multiCompanyIsEnabled && !!MultiCompanySelect.length ? MultiCompanySelect.join(',') : undefined,
            orgUnit: searchRequest.searchQuery.state.orgUnit ?? undefined,
            salesForcePhaseName: searchRequest.searchQuery.state.salesForcePhaseName ?? undefined,
            userIds: userIds || undefined,
            approvalStatus: approvalStatus ?? undefined,
            simDocumentCreated: simDocumentCreated ?? undefined,
            includeWorkflowSearch: isWorkflowSearch || undefined,
        };
    }

    async fetchResults(
        _searchConfiguration: ISearchConfiguration<IVendorSearchData>,
        searchRequest: ISearchRequest<IVendorSearchData>
    ): Promise<ISearchResponse> {
        this.setVendorSearchMode(searchRequest.searchQuery.state.searchMode ?? this.vendorSearchMode);

        const params: backendServices.Apis.GetTradingPartnersSearchParams = {
            ...this.buildSearchParams(searchRequest),
            $count: true,
            $top: searchRequest.pageSize,
            $skip: calculatePageOffset(searchRequest.pageSize, searchRequest.pageNumber),
        };

        const response = await this.vendorService.getVendorSearchResultsGrid(params);

        return {
            pageResults: response.items,
            totalResultCount: response.count,
        };
    }

    async exportData(
        _searchConfiguration: ISearchConfiguration<IVendorSearchData>,
        searchRequest: ISearchRequest<IVendorSearchData>,
        _exportMethod: string,
        filename: string
    ): Promise<void> {
        const { data } = await this.companyApi.getTradingPartnersSearchCSV(this.buildSearchParams(searchRequest));

        const blob = new Blob([data], { type: 'text/csv;charset=utf-8' });
        saveAs(blob, filename);
    }

    get key(): string {
        return 'VendorSearch'.concat(this.dependenciesWatcher.state);
    }

    private shouldUseVendorSearchWithCriteria(
        currentUser: IPortalUser,
        searchQuery: SimpleFieldRendererViewModel<IVendorSearchData>
    ) {
        return (
            searchQuery.state.searchMode !== VendorSearchMode.ConnectedSuppliers &&
            currentUser.extraFeatures.has('FE_TM_VENDOR_MANAGEMENT_ENABLED')
        );
    }
}

export const useVendorSearchService = createUseSearchService(VendorSearchService);
